import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil, Trash2, GripVertical, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { usePropertyKitCategories } from '@/hooks/inventory';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const formSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
});

type FormData = z.infer<typeof formSchema>;

interface CategoryFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CategoryFormModal({ open, onOpenChange }: CategoryFormModalProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const {
    categories,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategory,
    isCreating,
    isUpdating,
    isDeleting,
    isReordering,
  } = usePropertyKitCategories();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
    },
  });

  const handleStartEdit = (id: string, name: string) => {
    setEditingId(id);
    form.setValue('name', name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    form.reset();
  };

  const onSubmit = async (data: FormData) => {
    try {
      if (editingId) {
        await updateCategory({ id: editingId, name: data.name });
      } else {
        await createCategory({ name: data.name });
      }
      form.reset();
      setEditingId(null);
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCategory(id);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleMoveUp = async (currentIndex: number) => {
    if (currentIndex === 0 || isReordering) return;
    
    const current = categories[currentIndex];
    const target = categories[currentIndex - 1];
    
    // Use array indices as sort_order values for reliable swapping
    await reorderCategory({
      categoryId: current.id,
      newSortOrder: currentIndex - 1,
      swapCategoryId: target.id,
      swapSortOrder: currentIndex,
    });
  };

  const handleMoveDown = async (currentIndex: number) => {
    if (currentIndex === categories.length - 1 || isReordering) return;
    
    const current = categories[currentIndex];
    const target = categories[currentIndex + 1];
    
    // Use array indices as sort_order values for reliable swapping
    await reorderCategory({
      categoryId: current.id,
      newSortOrder: currentIndex + 1,
      swapCategoryId: target.id,
      swapSortOrder: currentIndex,
    });
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Gestionar Categorías</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Create/Edit form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex gap-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder={editingId ? 'Editar categoría...' : 'Nueva categoría...'}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-brand-primary hover:bg-brand-primary-hover"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editingId ? (
                  'Guardar'
                ) : (
                  <Plus className="h-4 w-4" />
                )}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={handleCancelEdit}>
                  Cancelar
                </Button>
              )}
            </form>
          </Form>

          {/* Categories list */}
          <div className="border rounded-lg divide-y max-h-[60vh] overflow-y-auto">
            {categories.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No hay categorías. Crea la primera arriba.
              </div>
            ) : (
              categories.map((category, index) => (
                <div
                  key={category.id}
                  className="flex items-center gap-2 p-3 hover:bg-accent/50"
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1">{category.name}</span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0 || isReordering}
                      className="h-8 w-8"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === categories.length - 1 || isReordering}
                      className="h-8 w-8"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleStartEdit(category.id, category.name)}
                      className="h-8 w-8"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
                          <AlertDialogDescription>
                            La categoría "{category.name}" se eliminará. Los artículos
                            asociados deberán ser reasignados a otra categoría.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(category.id)}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {isDeleting ? 'Eliminando...' : 'Eliminar'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
